import axios from 'axios';
import { getTokenCookie } from './utils';
import { compressData } from './utils/compressionUtils';
import { CUSTOM_EXTERNAL_ENABLED } from './constants';

// Global axios response interceptor (installed once at module load).
//
// Handles auth-related error codes from the server. Each branch below should
// be narrow and target a specific server-emitted error code so that other
// existing 401 handling (e.g. logout-on-expiry in containers/index.js) keeps
// working for non-matching responses.
axios.interceptors.response.use(
     (response) => response,
     (error) => {
          // MUST_RESETUP_2FA: user signed in via recovery code and the
          // server-side enforcement middleware (enforce-resetup) is blocking
          // sensitive routes until they re-enroll their authenticator. Send
          // them to the security page with a flag so the UI can prompt the
          // re-setup flow immediately. Hang the promise so the caller never
          // observes the 403 mid-redirect.
          if (
               error.response?.status === 403 &&
               error.response?.data?.error === 'MUST_RESETUP_2FA'
          ) {
               const redirectTo = error.response?.data?.redirectTo || '/my-account/profile/security';
               window.location.assign(`${redirectTo}?mustResetup=1`);
               return new Promise(() => {}); // hang the original promise
          }
          return Promise.reject(error);
     },
);

export const callApi = (endpoint, method = 'GET', data, token) => {
     let headers = null;

     if (token) {
          headers = { Authorization: `Bearer ${token}` };
     }

     let urlApi = process.env.REACT_APP_API;

     let options = {
          url: `${urlApi}/${endpoint}`,
          method,
          withCredentials: true,
     };

     if (headers) {
          options.headers = headers;
     }

     if (data !== null) {
          options.data = data;
     }

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callCompressApi = async (endpoint, method = 'GET', data, token) => {
     let headers = null;

     if (token) {
          headers = { Authorization: `Bearer ${token}` };
     }

     headers = {
          ...headers,
          'Content-Encoding': "zstd",
          'Content-Type': "application/binary"
     };

     let urlApi = process.env.REACT_APP_API;

     let options = {
          url: `${urlApi}/${endpoint}`,
          method,
     };

     if (headers) {
          options.headers = headers;
     }

     if (data !== null && typeof data !== 'undefined') {
          const compressed = await compressData(data)
          options.data = compressed;
     }

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

// Call api with a token which is got from cookie
export const callTokenApi = (endpoint, method = 'GET', data, cancelToken) => {
     const token = getTokenCookie();

     if (token) {
          let headers = null;
          headers = { Authorization: `Bearer ${token}` };

          let urlApi = process.env.REACT_APP_API;

          let options = {
               url: `${urlApi}/${endpoint}`,
               method,
               // Carry HTTP-only cookies (e.g. trusted_device) on authenticated
               // calls so the server can read and clear them via Set-Cookie.
               withCredentials: true,
          };

          if (headers) {
               options.headers = headers;
          }

          if (data !== null) {
               options.data = data;
          }

          // Add cancel token to axios to abort request if need
          if (cancelToken) {
               options.cancelToken = cancelToken;
          }

          return axios(options)
               .then((response) => response)
               .catch((error) => error.response);
     }
};

export const callCompressTokenApi = async (endpoint, method = 'GET', data, cancelToken) => {
     const token = getTokenCookie();
     if (token) {
          let headers = null;
          headers = {
               Authorization: `Bearer ${token}`,
               'Content-Encoding': "zstd",
               'Content-Type': "application/binary"
          };
          let urlApi = process.env.REACT_APP_API;

          let options = {
               url: `${urlApi}/${endpoint}`,
               method,
          };

          if (headers) {
               options.headers = headers;
          }

          if (data !== null && typeof data !== 'undefined') {
               const compressed = await compressData(data)
               options.data = compressed;
          }

          // Add cancel token to axios to abort request if need
          if (cancelToken) {
               options.cancelToken = cancelToken;
          }

          return axios(options)
               .then((response) => response)
               .catch((error) => error.response);
     }
};

export const callSalesforceApi = (endpoint, method = 'GET', data, cancelToken) => {
     const token = getTokenCookie();
     if (token) {
          let urlApi = process.env.REACT_APP_SALESFORCE_API;

          let headers = {
               Authorization: `Bearer ${token}`,
               'Content-Type': 'application/json',
          };

          let options = {
               url: `${urlApi}/${endpoint}`,
               method,
          };

          if (headers) {
               options.headers = headers;
          }

          if (data !== null) {
               options.data = data;
          }

          // Add cancel token to axios to abort request if need
          if (cancelToken) {
               options.cancelToken = cancelToken;
          }

          return axios(options)
               .then((response) => response)
               .catch((error) => error.response);
     }
};

export const callExternalDataApi = (endpoint, method = 'GET', data, cancelToken) => {
     // CES-OFF #76871: never call the retired Custom External service.
     if (!CUSTOM_EXTERNAL_ENABLED) {
          return Promise.resolve({ status: 410, data: { code: 410, message: 'Custom External Data has been removed.' } });
     }

     const token = getTokenCookie();
     if (token) {
          let urlApi = process.env.REACT_APP_EXTERNAL_DATA_API;
          const isFormData = data instanceof FormData;

          let headers = {
               Authorization: `Bearer ${token}`,
               ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          };

          let options = {
               url: `${urlApi}/${endpoint}`,
               method,
          };

          if (headers) {
               options.headers = headers;
          }

          if (data !== null) {
               options.data = data;
          }

          // Add cancel token to axios to abort request if need
          if (cancelToken) {
               options.cancelToken = cancelToken;
          }

          return axios(options)
               .then((response) => response)
               .catch((error) => error.response);
     }
};

const cancelTokenSources = {};

export const callTokenApiCancel = async (endpoint, method = 'GET', data, setCancelApi) => {
     const token = getTokenCookie();

     if (token) {
          let headers = null;
          headers = { Authorization: `Bearer ${token}` };

          let urlApi = process.env.REACT_APP_API;

          let options = {
               url: `${urlApi}/${endpoint}`,
               method,
          };

          if (headers) {
               options.headers = headers;
          }

          if (data !== null) {
               options.data = data;
          }

          if (cancelTokenSources[endpoint]) {
               cancelTokenSources[endpoint].cancel('Request cancelled due to a new request.');
          }

          cancelTokenSources[endpoint] = axios.CancelToken.source();
          options.cancelToken = cancelTokenSources[endpoint].token;
          if (setCancelApi) {
               setCancelApi(cancelTokenSources);
          }

          try {
               const response = await axios(options);
               return response;
          } catch (error) {
               if (axios.isCancel(error)) {
                    console.log('Request was cancelled:', error.message);
               } else {
                    console.error('API error:', error);
                    return error.response;
               }
               return error.response;
          }
     }
};

export const callCompressTokenApiCancel = async (endpoint, method = 'GET', data, setCancelApi) => {
     const token = getTokenCookie();

     if (token) {
          let headers = null;
          headers = {
               Authorization: `Bearer ${token}`,
               'Content-Encoding': "zstd",
               'Content-Type': "application/binary"
          };

          let urlApi = process.env.REACT_APP_API;

          let options = {
               url: `${urlApi}/${endpoint}`,
               method,
          };

          if (headers) {
               options.headers = headers;
          }

          if (data !== null && typeof data !== 'undefined') {
               const compressed = await compressData(data)
               options.data = compressed;
          }

          if (cancelTokenSources[endpoint]) {
               cancelTokenSources[endpoint].cancel('Request cancelled due to a new request.');
          }

          cancelTokenSources[endpoint] = axios.CancelToken.source();
          options.cancelToken = cancelTokenSources[endpoint].token;
          if (setCancelApi) {
               setCancelApi(cancelTokenSources);
          }

          try {
               const response = await axios(options);
               return response;
          } catch (error) {
               if (axios.isCancel(error)) {
                    console.log('Request was cancelled:', error.message);
               } else {
                    console.error('API error:', error);
                    return error.response;
               }
               return error.response;
          }
     }
};

export const callApiPreviewKV = (data) => {
     let urlApi = process.env.REACT_APP_PREVIEW_KV;

     let options = {
          url: `${urlApi}`,
          method: 'POST',
     };

     if (data !== null) {
          options.data = data;
     }

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callDomainValidation = (data) => {
     let urlApi = process.env.REACT_APP_API_DOMAIN_VALIDATION;

     let options = {
          url: urlApi,
          method: 'POST',
          data,
     };

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callApiPublishVersionLambda = (data) => {
     let urlApi = process.env.REACT_APP_API_PUBLISH_VERSION;

     let options = {
          url: urlApi,
          method: 'POST',
          data,
     };

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callApiGeneratePdf = (endpoint) => {
     let urlApi = process.env.REACT_APP_API;

     let options = {
          url: `${urlApi}/${endpoint}`,
          method: 'GET',
     };

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const makeRequest = (url, method, data, token) => {
     let headers = null;

     if (token) {
          headers = { Authorization: `Bearer ${token}` };
     }

     let options = {
          url,
          method,
     };

     if (headers) {
          options.headers = headers;
     }

     if (data !== null) {
          options.data = data;
     }

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callApiCrawlerStart = (data, isRunningV2) => {
     let urlApi = isRunningV2 ? process.env.REACT_APP_CRAWLER_START : process.env.REACT_APP_CRAWLER_START_V1;

     let options = {
          url: `${urlApi}`,
          method: 'POST',
     };

     if (data !== null) {
          options.data = data;
     }

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callApiCrawlPause = (data) => {
     let urlApi = process.env.REACT_APP_CRAWL_PAUSE;

     let options = {
          url: `${urlApi}`,
          method: 'POST',
     };

     if (data !== null) {
          options.data = data;
     }

     return axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const callApiCheckDomainCompany = async (data) => {
     let urlApi = process.env.REACT_APP_CHECK_COMPANY_DOMAIN || 'https://n4qalm5ada.execute-api.us-east-1.amazonaws.com/dev/check-domain-company';

     let options = {
          url: `${urlApi}`,
          method: 'POST',
     };

     if (data !== null) {
          options.data = data;
     }

     return await axios(options)
          .then((response) => response)
          .catch((error) => error.response);
};

export const uploadToS3WithProgress = async ({
     uploadUrl,
     file,
     uploadingProgressCallback,
     cancelCallback,
}) => {
     const options = {
          headers: {
               'Content-Type': file.type,
          },
          onUploadProgress: (event) => {
               if (uploadingProgressCallback && event.total) {
                    const percentComplete = (event.loaded / event.total) * 100;
                    uploadingProgressCallback(percentComplete);
               }
          }
     };

     if (cancelCallback) {
          const CancelToken = axios.CancelToken;
          const source = CancelToken.source();
          options.cancelToken = source.token;
          cancelCallback(source.cancel);
     };

     return axios.put(uploadUrl, file, options)
          .then((response) => response)
          .catch((error) => error.response);
};

